import { Button, Card, CardBody, CardTitle } from '@fold-and-flow/ui'
import { useForm } from '@tanstack/react-form'
import { useNavigate } from '@tanstack/react-router'
import { AnimatePresence, motion } from 'framer-motion'
import { useState } from 'react'
import { z } from 'zod'
import { trpc } from '#lib/trpc'

type Step = 'goals' | 'experience' | 'duration' | 'injuries'

const GOAL_VALUES = [
  'strength',
  'flexibility',
  'stress_relief',
  'mindfulness',
  'injury_recovery',
  'general_wellness',
] as const

const EXPERIENCE_LEVEL_VALUES = ['beginner', 'intermediate', 'advanced'] as const

const onboardingFormSchema = z.object({
  goals: z.array(z.enum(GOAL_VALUES)).min(1),
  experienceLevel: z.enum(EXPERIENCE_LEVEL_VALUES),
  preferredDuration: z.union([z.literal(20), z.literal(30), z.literal(45), z.literal(60)]),
  injuries: z.string(),
})

type OnboardingFormValues = z.infer<typeof onboardingFormSchema>

const defaultValues: OnboardingFormValues = {
  goals: ['general_wellness'],
  experienceLevel: 'beginner',
  preferredDuration: 30,
  injuries: '',
}

const STEPS: Step[] = ['goals', 'experience', 'duration', 'injuries']

const GOALS: { value: OnboardingFormValues['goals'][number]; label: string }[] = [
  { value: 'strength', label: 'Build strength' },
  { value: 'flexibility', label: 'Improve flexibility' },
  { value: 'stress_relief', label: 'Relieve stress' },
  { value: 'mindfulness', label: 'Mindfulness & presence' },
  { value: 'injury_recovery', label: 'Injury recovery' },
  { value: 'general_wellness', label: 'General wellness' },
]

const EXPERIENCE: {
  value: NonNullable<OnboardingFormValues['experienceLevel']>
  label: string
  description: string
}[] = [
  {
    value: 'beginner',
    label: 'Beginner',
    description: 'New to yoga or returning after a long break',
  },
  { value: 'intermediate', label: 'Intermediate', description: 'Comfortable with common poses' },
  { value: 'advanced', label: 'Advanced', description: 'Strong practice, exploring depth' },
]

const DURATIONS: { value: OnboardingFormValues['preferredDuration']; label: string }[] = [
  { value: 20, label: '20 min' },
  { value: 30, label: '30 min' },
  { value: 45, label: '45 min' },
  { value: 60, label: '60 min' },
]

const slide = {
  initial: { opacity: 0, x: 24 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -24 },
  transition: { duration: 0.3, ease: 'easeInOut' as const },
}

export function OnboardingPage() {
  const navigate = useNavigate()
  const [step, setStep] = useState<Step>('goals')

  const upsertProfile = trpc.profile.upsert.useMutation({
    onSuccess: () => navigate({ to: '/' }),
  })

  const form = useForm({
    defaultValues,
    validators: {
      onSubmit: onboardingFormSchema,
    },
    onSubmit: async ({ value }) => {
      const parsed = onboardingFormSchema.parse(value)

      await upsertProfile.mutateAsync({
        goals: parsed.goals,
        experienceLevel: parsed.experienceLevel,
        preferredDuration: parsed.preferredDuration,
        injuries: parsed.injuries
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
      })
    },
  })

  function advance() {
    const idx = STEPS.indexOf(step)
    const next = STEPS[idx + 1]
    if (next) setStep(next)
  }

  return (
    <div className="max-w-lg mx-auto">
      {/* Step indicator */}
      <div className="flex gap-1.5 mb-8">
        {STEPS.map((s, i) => (
          <div
            key={s}
            className={`h-1 flex-1 rounded-full transition-colors duration-300 ${
              STEPS.indexOf(step) >= i ? 'bg-terracotta-400' : 'bg-cream-200'
            }`}
          />
        ))}
      </div>

      <AnimatePresence mode="wait">
        {step === 'goals' && (
          <motion.div key="goals" {...slide}>
            <Card elevated>
              <CardBody>
                <CardTitle className="mb-1">What brings you to your mat?</CardTitle>
                <p className="text-sm text-warm-gray-400 mb-6">Select all that apply</p>
                <form.Field name="goals">
                  {(field) => (
                    <div className="flex flex-col gap-2.5 mb-8">
                      {GOALS.map((g) => {
                        const selected = field.state.value.includes(g.value)
                        return (
                          <button
                            key={g.value}
                            type="button"
                            onClick={() =>
                              field.handleChange(
                                selected
                                  ? field.state.value.filter((v) => v !== g.value)
                                  : [...field.state.value, g.value]
                              )
                            }
                            className={`px-4 py-3 rounded-xl text-left text-sm border transition-all ${
                              selected
                                ? 'border-terracotta-400 bg-terracotta-400/5 text-terracotta-600 font-medium'
                                : 'border-cream-200 bg-white text-warm-gray-700 hover:border-warm-gray-400'
                            }`}
                          >
                            {g.label}
                          </button>
                        )
                      })}
                    </div>
                  )}
                </form.Field>
                <form.Subscribe selector={(s) => s.values.goals}>
                  {(goals) => (
                    <Button
                      size="lg"
                      className="w-full"
                      disabled={goals.length === 0}
                      onClick={advance}
                    >
                      Continue →
                    </Button>
                  )}
                </form.Subscribe>
              </CardBody>
            </Card>
          </motion.div>
        )}

        {step === 'experience' && (
          <motion.div key="experience" {...slide}>
            <Card elevated>
              <CardBody>
                <CardTitle className="mb-6">Your experience level?</CardTitle>
                <form.Field name="experienceLevel">
                  {(field) => (
                    <div className="flex flex-col gap-3 mb-8">
                      {EXPERIENCE.map((e) => (
                        <button
                          key={e.value}
                          type="button"
                          onClick={() => field.handleChange(e.value)}
                          className={`px-5 py-4 rounded-2xl text-left border transition-all ${
                            field.state.value === e.value
                              ? 'border-terracotta-400 bg-terracotta-400/5'
                              : 'border-cream-200 bg-white hover:border-warm-gray-400'
                          }`}
                        >
                          <div className="font-medium text-warm-gray-800">{e.label}</div>
                          <div className="text-sm text-warm-gray-400 mt-0.5">{e.description}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </form.Field>
                <form.Subscribe selector={(s) => s.values.experienceLevel}>
                  {(exp) => (
                    <Button size="lg" className="w-full" disabled={!exp} onClick={advance}>
                      Continue →
                    </Button>
                  )}
                </form.Subscribe>
              </CardBody>
            </Card>
          </motion.div>
        )}

        {step === 'duration' && (
          <motion.div key="duration" {...slide}>
            <Card elevated>
              <CardBody>
                <CardTitle className="mb-6">Preferred session length?</CardTitle>
                <form.Field name="preferredDuration">
                  {(field) => (
                    <div className="grid grid-cols-2 gap-3 mb-8">
                      {DURATIONS.map((d) => (
                        <button
                          key={d.value}
                          type="button"
                          onClick={() => field.handleChange(d.value)}
                          className={`py-5 rounded-2xl text-center border text-sm font-medium transition-all ${
                            field.state.value === d.value
                              ? 'border-terracotta-400 bg-terracotta-400/5 text-terracotta-600'
                              : 'border-cream-200 bg-white text-warm-gray-700 hover:border-warm-gray-400'
                          }`}
                        >
                          {d.label}
                        </button>
                      ))}
                    </div>
                  )}
                </form.Field>
                <Button size="lg" className="w-full" onClick={advance}>
                  Continue →
                </Button>
              </CardBody>
            </Card>
          </motion.div>
        )}

        {step === 'injuries' && (
          <motion.div key="injuries" {...slide}>
            <Card elevated>
              <CardBody>
                <CardTitle className="mb-1">Any injuries or limitations?</CardTitle>
                <p className="text-sm text-warm-gray-400 mb-6">
                  Optional — helps us keep your practice safe
                </p>
                <form.Field name="injuries">
                  {(field) => (
                    <textarea
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                      placeholder="e.g. left knee, lower back, wrist sensitivity"
                      className="w-full min-h-28 rounded-xl border border-cream-200 bg-white px-4 py-3 text-sm text-warm-gray-800 placeholder:text-warm-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-terracotta-400 mb-8"
                    />
                  )}
                </form.Field>
                <form.Subscribe selector={(s) => s.isSubmitting}>
                  {(isSubmitting) => (
                    <Button
                      size="lg"
                      className="w-full"
                      loading={isSubmitting}
                      onClick={() => form.handleSubmit()}
                    >
                      Start my practice →
                    </Button>
                  )}
                </form.Subscribe>
              </CardBody>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
